import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Loader2, ChevronDown, ChevronRight, Pencil, CheckCircle2, Clock, Truck } from "lucide-react";
import EditOrderDialog from "@/components/orders/EditOrderDialog";

const API = getApiUrl();

function currencySymbol(code) {
  return ({ GBP: "£", USD: "$", EUR: "€" }[code] || "£");
}

export default function OrdersBySupplierAdmin() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // { order, supplierId, supplierName, vendorEmail }

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/orders/by-supplier`, { withCredentials: true });
      setGroups(data.groups || []);
    } catch {
      toast.error("Failed to load orders by supplier");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <section className="border border-border rounded bg-background" data-testid="orders-by-supplier-section">
      <header className="px-4 py-3 border-b bg-muted/40">
        <h3 className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4" /> Orders by supplier</h3>
        <p className="text-xs text-muted-foreground">Every paid order's items, grouped by supplier — newest activity first. Click an order to edit or resend the instruction email.</p>
      </header>

      {groups.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground" data-testid="orders-by-supplier-empty">No paid orders yet.</div>
      ) : (
        groups.map((g, gi) => {
          const isOpen = expanded[g.supplier_id] !== false; // default open
          return (
            <div key={g.supplier_id || `none-${gi}`} className="border-t border-border">
              <button
                type="button"
                onClick={() => setExpanded((p) => ({ ...p, [g.supplier_id]: !isOpen }))}
                className="w-full flex items-center justify-between px-4 py-2 hover:bg-muted/30 text-left"
                data-testid={`orders-by-supplier-group-${g.supplier_id || gi}`}
              >
                <span className="flex items-center gap-2 font-medium text-sm">
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  {g.supplier_name}
                </span>
                <span className="text-xs text-muted-foreground">{g.orders.length} order{g.orders.length !== 1 ? "s" : ""}</span>
              </button>
              {isOpen && (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="p-2 pl-8">Placed</th>
                      <th className="p-2">Order</th>
                      <th className="p-2">Property</th>
                      <th className="p-2">Items</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2">Acknowledged</th>
                      <th className="p-2 text-right pr-4">Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.orders.map((o) => (
                      <tr key={`${g.supplier_id}-${o.order_id}`} className="border-t border-border/60 align-top" data-testid={`orders-by-supplier-row-${o.order_id}`}>
                        <td className="p-2 pl-8 text-xs whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
                        <td className="p-2 font-mono text-xs">#{(o.order_id || "").slice(0, 8).toUpperCase()}</td>
                        <td className="p-2 text-xs max-w-[180px] truncate" title={o.property_title}>{o.property_title || "—"}</td>
                        <td className="p-2 text-xs">
                          {o.items.map((it, i) => (<div key={i}>{it.quantity} × {it.name}</div>))}
                        </td>
                        <td className="p-2 text-right whitespace-nowrap text-sm font-medium">{currencySymbol(o.currency)}{Number(o.supplier_total).toFixed(2)}</td>
                        <td className="p-2">
                          {!o.supplier_notified_at ? (
                            <span className="text-[11px] text-muted-foreground">Not sent</span>
                          ) : o.supplier_acknowledged_at ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid={`orders-by-supplier-ack-${o.order_id}`}>
                              <CheckCircle2 className="h-3 w-3" /> Acknowledged
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" data-testid={`orders-by-supplier-ack-${o.order_id}`}>
                              <Clock className="h-3 w-3" /> Awaiting
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-right pr-4">
                          <button
                            type="button"
                            onClick={() => setEditing({
                              order: o,
                              supplierId: g.supplier_id,
                              supplierName: g.supplier_name,
                              vendorEmail: o.vendor_email,
                            })}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            data-testid={`orders-by-supplier-manage-${o.order_id}`}
                          >
                            <Pencil className="h-3 w-3" /> Manage
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}

      <EditOrderDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        order={editing?.order}
        apiBase="/admin/orders"
        onSaved={load}
        onDeleted={load}
        notifySupplier={
          editing
            ? { supplierId: editing.supplierId, supplierName: editing.supplierName, vendorEmail: editing.vendorEmail }
            : null
        }
      />
    </section>
  );
}
